import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Tabs, Tab, Paper, Grid, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, TextField, InputAdornment
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import QuizIcon from "@mui/icons-material/Quiz";
import FolderIcon from "@mui/icons-material/Folder";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { fetchData, postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import { exportToCSV, filterRows } from "../../utils/tableUtils";

const fallbackByTab = {
  0: [
    { id: "d1", fileName: "Tamil Reading Guide.pdf", type: "Guidelines", createdAt: "2025-01-15T00:00:00Z", fileSize: 204800, url: "" },
    { id: "d2", fileName: "Grammar Worksheet.pdf", type: "Assignment", createdAt: "2025-01-20T00:00:00Z", fileSize: 153600, url: "" },
    { id: "d3", fileName: "Project Instructions.pdf", type: "Instructions", createdAt: "2025-02-01T00:00:00Z", fileSize: 102400, url: "" },
  ],
  1: [
    { id: "t1", title: "Tamil Mozhi Payirchi - Level 1", subjectName: "Tamil", type: "Textbook", url: "" },
    { id: "t2", title: "Tamil Ilakkanam - Level 2", subjectName: "Tamil", type: "Textbook", url: "" },
    { id: "t3", title: "Tamil Padippu - Level 3", subjectName: "Tamil", type: "Workbook", url: "" },
  ],
  2: [
    { id: "q1", title: "Term 1 Q-Paper 2024", type: "QPaper", createdAt: "2025-03-01T00:00:00Z", url: "" },
    { id: "q2", title: "Mid-Term Q-Paper 2024", type: "QPaper", createdAt: "2025-06-01T00:00:00Z", url: "" },
    { id: "q3", title: "Term 2 Q-Paper 2024", type: "QPaper", createdAt: "2025-09-01T00:00:00Z", url: "" },
  ],
  3: [
    { id: "f1", fileName: "Tamil Flipbook Vol 1", type: "Flipbook", createdAt: "2025-01-10T00:00:00Z", url: "" },
    { id: "f2", fileName: "Tamil Flipbook Vol 2", type: "Flipbook", createdAt: "2025-03-10T00:00:00Z", url: "" },
  ],
  4: [
    { id: "p1", fileName: "Science Project Guidelines", type: "Project", createdAt: "2025-02-15T00:00:00Z", url: "" },
    { id: "p2", fileName: "Tamil Essay Project", type: "Project", createdAt: "2025-04-01T00:00:00Z", url: "" },
  ],
};

const TAB_ICONS = [
  <AssignmentIcon />,
  <MenuBookIcon />,
  <QuizIcon />,
  <FolderIcon />,
  <FolderIcon />,
];
const TAB_LABELS = ["Homework", "Reading", "Model Question Paper", "Flipbook", "Project"];

const ClassroomResources = () => {
  const [tabValue, setTabValue] = useState(0);
  const [rows, setRows] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const uploadInputRef = useRef(null);
  const [uploadTargetRow, setUploadTargetRow] = useState(null);

  const loadResources = useCallback(async (tab) => {
    const typeMap = { 0: "homework", 1: "reading", 2: "qpaper", 3: "flipbook", 4: "project" };
    const docType = typeMap[tab];
    try {
      const response = await fetchData(`admin/document/list?type=${docType}&limit=50`);
      if (response?.data) {
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        setRows(data.length > 0 ? data : fallbackByTab[tab] || []);
      } else {
        setRows(fallbackByTab[tab] || []);
      }
    } catch (_) {
      setRows(fallbackByTab[tab] || []);
    }
  }, []);

  useEffect(() => { loadResources(tabValue); }, [tabValue, loadResources]);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    setRows([]);
    setFilterText("");
  };

  const handleToastClose = () => setToast({ ...toast, open: false });

  const handleDownload = (url, fileName) => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "document";
    link.target = "_blank";
    link.click();
  };

  const handleUploadClick = (row) => {
    setUploadTargetRow(row);
    if (uploadInputRef.current) uploadInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !uploadTargetRow) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("documentId", uploadTargetRow.id || "");
    formData.append("fileName", uploadTargetRow.fileName || uploadTargetRow.title || "");
    try {
      await postData("student/classroom/upload", formData);
      setToast({ open: true, severity: "success", message: `"${file.name}" uploaded successfully!` });
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Upload failed. Please try again." });
    } finally {
      e.target.value = "";
      setUploadTargetRow(null);
    }
  };

  // Filter rows — operates on plain object data from API
  const filteredRows = filterRows(rows, filterText);

  const handleExport = () => {
    const tabLabel = TAB_LABELS[tabValue].toLowerCase().replace(/[\s/]+/g, "_");
    const exportCols = [
      { label: "File Name", field: "fileName" },
      { label: "Type", field: "type" },
      { label: "Upload Date", field: "uploadDate" },
    ];
    const exportData = filteredRows.map((r) => ({
      fileName: r.fileName || r.title || "—",
      type: r.type || r.documentType || "Document",
      uploadDate: r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-GB") : "—",
    }));
    exportToCSV(exportCols, exportData, `classroom_${tabLabel}.csv`);
  };

  const styles = {
    container: { textAlign: "left", padding: "1rem" },
    headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 },
    badge: { backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" },
    actionBtn: { border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px", padding: "6px 14px" },
    tabContainer: { backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", overflow: "hidden" },
    tableHead: { backgroundColor: "#f8f9fc" },
    tableHeadCell: { fontWeight: "bold", fontSize: "13px", color: "#374151", borderBottom: "1px solid #E5E7EB" },
    tableRow: { "&:hover": { backgroundColor: "#f8f9fc" }, "&:last-child td": { border: 0 } },
    downloadBtn: { background: "linear-gradient(90deg, #4A7BA8, #244C77)", color: "#fff", fontSize: "12px", textTransform: "none", padding: "4px 12px", borderRadius: "6px", minWidth: "unset" },
    emptyState: { textAlign: "center", py: 8, color: "#8F95B2" },
    fileIcon: { color: "#f44336", fontSize: "20px", mr: 1 },
    bookCard: { p: 2, borderRadius: "12px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", cursor: "pointer", transition: "transform 0.2s", "&:hover": { transform: "translateY(-2px)" } },
    bookCover: { width: "60px", height: "80px", backgroundColor: "#e0d6ff", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 8px" },
  };

  const renderDocumentsTable = () => (
    filteredRows.length === 0 ? (
      <Box sx={styles.emptyState}><Typography>No resources found</Typography></Box>
    ) : (
      <TableContainer>
        <Table>
          <TableHead sx={styles.tableHead}>
            <TableRow>
              <TableCell sx={styles.tableHeadCell}>#</TableCell>
              <TableCell sx={styles.tableHeadCell}>File Name</TableCell>
              <TableCell sx={styles.tableHeadCell}>Type</TableCell>
              <TableCell sx={styles.tableHeadCell}>Upload Date</TableCell>
              <TableCell sx={styles.tableHeadCell}>Size</TableCell>
              <TableCell sx={styles.tableHeadCell}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row, index) => (
              <TableRow key={row.id || index} sx={styles.tableRow}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PictureAsPdfIcon sx={styles.fileIcon} />
                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{row.fileName || row.title || "—"}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={row.type || row.documentType || "Document"} size="small" sx={{ backgroundColor: "#EAF1F8", color: "#244C77", fontSize: "11px" }} />
                </TableCell>
                <TableCell sx={{ fontSize: "13px", color: "#6B7280" }}>
                  {row.createdAt ? new Date(row.createdAt).toLocaleDateString("en-GB") : "—"}
                </TableCell>
                <TableCell sx={{ fontSize: "13px", color: "#6B7280" }}>
                  {row.fileSize ? `${(row.fileSize / 1024).toFixed(1)} KB` : "—"}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "nowrap" }}>
                    <Button size="small" startIcon={<DownloadIcon />} sx={styles.downloadBtn} onClick={() => handleDownload(row.url || row.fileUrl, row.fileName)}>
                      Download
                    </Button>
                    {tabValue === 0 && (
                      <Button size="small" startIcon={<UploadIcon />}
                        sx={{ ...styles.downloadBtn, background: "linear-gradient(90deg, #43a047, #1b5e20)" }}
                        onClick={() => handleUploadClick(row)}>
                        Upload
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  );

  const renderTextbooksGrid = () => (
    filteredRows.length === 0 ? (
      <Box sx={{ ...styles.emptyState, p: 4 }}><Typography>No textbooks found</Typography></Box>
    ) : (
      <Grid container spacing={2} sx={{ p: 2 }}>
        {filteredRows.map((book, index) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={book.id || index}>
            <Paper elevation={0} sx={styles.bookCard}>
              <Box sx={styles.bookCover}>
                <MenuBookIcon sx={{ color: "#244C77", fontSize: "28px" }} />
              </Box>
              <Typography sx={{ fontSize: "12px", fontWeight: "bold", mb: 0.5 }}>{book.title || book.fileName || "Textbook"}</Typography>
              <Typography sx={{ fontSize: "11px", color: "#8F95B2", mb: 1 }}>{book.subjectName || book.type || "—"}</Typography>
              <Button size="small" startIcon={<DownloadIcon />} sx={{ ...styles.downloadBtn, fontSize: "11px" }} onClick={() => handleDownload(book.url || book.fileUrl, book.title)}>
                Download
              </Button>
            </Paper>
          </Grid>
        ))}
      </Grid>
    )
  );

  const renderQPaperGrid = () => (
    filteredRows.length === 0 ? (
      <Box sx={{ ...styles.emptyState, p: 4 }}><Typography>No question papers found</Typography></Box>
    ) : (
      <Grid container spacing={2} sx={{ p: 2 }}>
        {filteredRows.map((paper, index) => (
          <Grid item xs={12} sm={6} md={4} key={paper.id || index}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", border: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <QuizIcon sx={{ color: "#244C77" }} />
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{paper.title || paper.fileName || "Q-Paper"}</Typography>
                  <Typography sx={{ fontSize: "11px", color: "#8F95B2" }}>
                    {paper.createdAt ? new Date(paper.createdAt).toLocaleDateString("en-GB") : "—"}
                  </Typography>
                </Box>
              </Box>
              <Tooltip title="Download">
                <IconButton size="small" onClick={() => handleDownload(paper.url || paper.fileUrl, paper.title)} sx={{ color: "#244C77" }}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          </Grid>
        ))}
      </Grid>
    )
  );

  const renderTabContent = () => {
    switch (tabValue) {
      case 0: return renderDocumentsTable();
      case 1: return renderTextbooksGrid();
      case 2: return renderQPaperGrid();
      case 3:
      case 4: return renderDocumentsTable();
      default: return null;
    }
  };

  return (
    <div style={styles.container}>
      <Toast open={toast.open} handleClose={handleToastClose} severity={toast.severity} message={toast.message} />
      {/* Hidden file input for upload */}
      <input
        ref={uploadInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept=".pdf,.doc,.docx,.jpg,.png,.zip"
      />

      <p style={{ color: "rgba(0, 0, 0, 0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Classroom Resources
      </p>

      <Box sx={styles.headerRow}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <h2 style={{ margin: 0 }}>Classroom Resources</h2>
          <Chip label={`${filteredRows.length} Items`} sx={styles.badge} />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button startIcon={<FilterListIcon />} sx={{ ...styles.actionBtn, ...(showFilter ? { backgroundColor: "#f0edff", color: "#244C77", borderColor: "#244C77" } : {}) }} onClick={() => setShowFilter((v) => !v)}>Filters</Button>
          <Button startIcon={<FileDownloadOutlinedIcon />} sx={styles.actionBtn} onClick={handleExport}>Export</Button>
        </Box>
      </Box>

      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small" fullWidth placeholder="Search by file name, type..."
            value={filterText} onChange={(e) => setFilterText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#D5D7DA" } } }}
          />
        </Box>
      )}

      <Paper elevation={0} sx={styles.tabContainer}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: "1px solid #E5E7EB",
            px: 2,
            "& .MuiTab-root": { textTransform: "none", fontSize: "14px", fontWeight: 500, color: "#6B7280", minHeight: "52px", gap: 0.5 },
            "& .Mui-selected": { color: "#244C77 !important", fontWeight: "bold" },
            "& .MuiTabs-indicator": { backgroundColor: "#244C77" },
          }}
        >
          {TAB_LABELS.map((label, index) => (
            <Tab key={label} label={label} icon={TAB_ICONS[index]} iconPosition="start" />
          ))}
        </Tabs>

        <Box sx={{ minHeight: "300px" }}>
          {renderTabContent()}
        </Box>
      </Paper>
    </div>
  );
};

export default ClassroomResources;
